import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import logo from '../../logo.png';
import { BarChart2, Download, RefreshCw, Lock } from 'lucide-react';

export default function ReportesAvanzados() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const puedeAcceder = ["superadmin","coordinador_general","coordinador_territorial","coordinador_sectorial","auditor","movilizador"].includes(user?.rol);

  const [tab, setTab] = useState('general');
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReporte = useCallback(async (tipo) => {
    if (!puedeAcceder) return;
    try {
      setLoading(true);
      const res = await api.get(`/reportes/${tipo}`);
      setDatos(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [puedeAcceder]);

  useEffect(() => {
    if (puedeAcceder) fetchReporte('general');
  }, [fetchReporte, puedeAcceder]);

  const handleTab = (nuevoTab) => {
    setTab(nuevoTab);
    fetchReporte(nuevoTab);
  };

  const descargarCSV = (datos, nombre) => {
    if (!datos || datos.length === 0) return;
    const headers = Object.keys(datos[0]).join(',');
    const rows = datos.map(r => Object.values(r).map(v => `"${v ?? ''}"`).join(','));
    const csv = [headers, ...rows].join('\n');
    const link = document.createElement('a');
    link.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    link.download = `${nombre}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Roles que pueden ver talento e inventario en reportes
  const puedeVerTalento    = ['superadmin', 'coordinador_general'].includes(user?.rol);
  const puedeVerInventario = ['superadmin', 'coordinador_general'].includes(user?.rol);

  const todasLasTabs = [
    { key: 'general',      label: '📊 General',       visible: true },
    { key: 'secciones',    label: '🗺 Secciones',      visible: true },
    { key: 'estructuras',  label: '🏛 Estructuras',    visible: true },
    { key: 'talento',      label: '⭐ Talento',        visible: puedeVerTalento },
    { key: 'inventario',   label: '📦 Inventario',     visible: puedeVerInventario },
    { key: 'duplicidades', label: '⚠️ Duplicidades',   visible: true },
    { key: 'visitas',      label: '🚪 Visitas',        visible: true },
  ];
  const tabs = todasLasTabs.filter(t => t.visible);

  // ── Acceso denegado ──
  if (!puedeAcceder) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="text-white px-6 py-4 flex items-center justify-between shadow-lg"
          style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)' }}>
          <img src={logo} alt="DOMINIUM" className="h-10" />
          <button onClick={() => navigate('/dashboard')}
            className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm transition">
            ← Volver al Dashboard
          </button>
        </header>
        <div className="flex flex-col items-center justify-center min-h-[70vh]">
          <Lock className="text-gray-300 mb-4" size={56} />
          <p className="text-xl font-semibold text-gray-500">Acceso restringido</p>
          <p className="text-gray-400 text-sm mt-2">Solo el Super Administrador puede ver los reportes avanzados.</p>
          <button onClick={() => navigate('/dashboard')}
            className="mt-6 px-6 py-2 rounded-lg text-white text-sm font-medium hover:opacity-90 transition"
            style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="text-white px-6 py-4 flex items-center justify-between shadow-lg"
        style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)' }}>
        <img src={logo} alt="DOMINIUM" className="h-10" />
        <button onClick={() => navigate('/dashboard')}
          className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm transition">
          ← Volver al Dashboard
        </button>
      </header>

      <main className="p-6 max-w-7xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <BarChart2 className="text-green-600" size={28} />
              Reportes Avanzados
            </h2>
            <p className="text-gray-500 text-sm">Análisis completo del sistema VIRTUS</p>
          </div>
          <button onClick={() => fetchReporte(tab)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition text-sm">
            <RefreshCw size={16} /> Actualizar
          </button>
        </div>

        <div className="flex gap-1 mb-6 overflow-x-auto border-b border-gray-200">
          {tabs.map(t => (
            <button key={t.key} onClick={() => handleTab(t.key)}
              className={`px-4 py-2 font-medium border-b-2 transition whitespace-nowrap text-sm ${
                tab === t.key ? 'border-green-600 text-green-600' : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <p className="text-gray-500">Cargando reporte...</p>
          </div>
        )}

        {!loading && datos && (
          <div>
            {tab === 'general' && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                  { label: '📋 Total Capturas',    valor: datos.promovidos?.total || 0,  color: 'border-blue-500' },
                  { label: '✅ Promovidos Activos', valor: datos.activos?.total || 0,     color: 'border-green-500' },
                  { label: '⚠️ Duplicidades',       valor: datos.duplicidades?.total || 0, color: 'border-red-500' },
                  { label: '🏛 Estructuras',        valor: datos.estructuras?.total || 0, color: 'border-purple-500' },
                  { label: '⭐ Banco Talento',      valor: datos.talento?.total || 0,     color: 'border-yellow-500' },
                  { label: '🚪 Visitas',            valor: datos.visitas?.total || 0,     color: 'border-orange-500' },
                  { label: '📦 Productos',          valor: datos.inventario?.total || 0,  color: 'border-teal-500' },
                  { label: '📊 Stock Total',        valor: datos.inventario?.stock || 0,  color: 'border-indigo-500' }
                ].map((item, i) => (
                  <div key={i} className={`bg-white rounded-xl shadow-sm p-4 border-l-4 ${item.color}`}>
                    <p className="text-xs text-gray-500">{item.label}</p>
                    <p className="text-2xl font-bold text-gray-800">{item.valor}</p>
                  </div>
                ))}
              </div>
            )}

            {tab === 'secciones' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-lg text-gray-800">Reporte por Secciones ({datos.secciones?.length || 0})</h3>
                  <button onClick={() => descargarCSV(datos.secciones, 'reporte_secciones')}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition">
                    <Download size={16} /> Descargar CSV
                  </button>
                </div>
                <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
                  <table className="w-full text-sm min-w-[700px]">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Sección</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Municipio</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Meta</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Activos</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Capturas</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Visitas</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">% Meta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datos.secciones?.map((s, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 font-bold">{s.numero_seccion}</td>
                          <td className="py-3 px-4">{s.municipio}</td>
                          <td className="py-3 px-4">{s.meta_votos || '—'}</td>
                          <td className="py-3 px-4 text-green-600 font-bold">{s.promovidos_activos}</td>
                          <td className="py-3 px-4">{s.total_capturas}</td>
                          <td className="py-3 px-4">{s.visitas}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div className="w-20 bg-gray-200 rounded-full h-2">
                                <div className="bg-green-500 h-2 rounded-full"
                                  style={{ width: `${Math.min(s.porcentaje_meta, 100)}%` }} />
                              </div>
                              <span className="text-xs font-medium">{s.porcentaje_meta}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === 'estructuras' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-lg text-gray-800">Reporte por Estructuras ({datos.estructuras?.length || 0})</h3>
                  <button onClick={() => descargarCSV(datos.estructuras, 'reporte_estructuras')}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition">
                    <Download size={16} /> Descargar CSV
                  </button>
                </div>
                <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
                  <table className="w-full text-sm min-w-[700px]">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Estructura</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Tipo</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Sección</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Capturas</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Activos</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Visitas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datos.estructuras?.map((e, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium">{e.nombre_estructura}</td>
                          <td className="py-3 px-4 capitalize">{e.tipo_estructura}</td>
                          <td className="py-3 px-4">{e.numero_seccion || '—'}</td>
                          <td className="py-3 px-4">{e.total_capturas}</td>
                          <td className="py-3 px-4 text-green-600 font-bold">{e.promovidos_activos}</td>
                          <td className="py-3 px-4">{e.visitas}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === 'talento' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-lg text-gray-800">Reporte de Talento</h3>
                  <button onClick={() => descargarCSV(datos.perfiles, 'reporte_talento')}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition">
                    <Download size={16} /> Descargar CSV
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-white rounded-xl shadow-sm p-4">
                    <h4 className="font-semibold text-gray-700 mb-3">Por Estatus</h4>
                    {datos.por_estatus?.map((e, i) => (
                      <div key={i} className="flex justify-between items-center py-1 border-b last:border-0">
                        <span className="text-sm capitalize">{e.estatus_talento?.replace('_', ' ')}</span>
                        <span className="font-bold text-green-600">{e.total}</span>
                      </div>
                    ))}
                  </div>
                  <div className="bg-white rounded-xl shadow-sm p-4">
                    <h4 className="font-semibold text-gray-700 mb-3">Por Área</h4>
                    {datos.por_area?.map((a, i) => (
                      <div key={i} className="flex justify-between items-center py-1 border-b last:border-0">
                        <span className="text-sm capitalize">{a.area_interes}</span>
                        <span className="font-bold text-blue-600">{a.total}</span>
                      </div>
                    ))}
                  </div>
                  <div className="bg-white rounded-xl shadow-sm p-4">
                    <h4 className="font-semibold text-gray-700 mb-3">Por Ocupación</h4>
                    {datos.por_ocupacion?.map((o, i) => (
                      <div key={i} className="flex justify-between items-center py-1 border-b last:border-0">
                        <span className="text-sm capitalize">{o.ocupacion_general}</span>
                        <span className="font-bold text-purple-600">{o.total}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
                  <table className="w-full text-sm min-w-[700px]">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Nombre</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Teléfono</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Ocupación</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Área</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Estatus</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datos.perfiles?.map((p, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium">{p.nombre} {p.apellido_paterno}</td>
                          <td className="py-3 px-4">{p.telefono_principal}</td>
                          <td className="py-3 px-4 capitalize">{p.ocupacion_general || '—'}</td>
                          <td className="py-3 px-4 capitalize">{p.area_interes || '—'}</td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium capitalize">
                              {p.estatus_talento?.replace('_', ' ')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === 'inventario' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-lg text-gray-800">Reporte de Inventario</h3>
                  <button onClick={() => descargarCSV(datos.productos, 'reporte_inventario')}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition">
                    <Download size={16} /> Descargar CSV
                  </button>
                </div>
                <div className="bg-white rounded-xl shadow-sm overflow-x-auto mb-6">
                  <h4 className="font-semibold text-gray-700 p-4 border-b">Stock por Producto</h4>
                  <table className="w-full text-sm min-w-[700px]">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Producto</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Categoría</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Stock Actual</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Entradas</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Salidas</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datos.productos?.map((p, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium">{p.nombre_producto}</td>
                          <td className="py-3 px-4 capitalize">{p.categoria || '—'}</td>
                          <td className="py-3 px-4 font-bold">{p.stock_actual} {p.unidad_medida}</td>
                          <td className="py-3 px-4 text-green-600">+{p.total_entradas}</td>
                          <td className="py-3 px-4 text-red-600">-{p.total_salidas}</td>
                          <td className="py-3 px-4">
                            {p.stock_actual <= p.stock_minimo ? (
                              <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">⚠ Stock bajo</span>
                            ) : (
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">✓ OK</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
                  <h4 className="font-semibold text-gray-700 p-4 border-b">Distribución por Sección</h4>
                  <table className="w-full text-sm min-w-[700px]">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Sección</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Producto</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Cantidad Entregada</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Entregas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datos.salidas_por_seccion?.length === 0 ? (
                        <tr><td colSpan="4" className="py-6 text-center text-gray-400">Sin distribución registrada</td></tr>
                      ) : datos.salidas_por_seccion?.map((s, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 font-bold">{s.numero_seccion || 'Sin sección'}</td>
                          <td className="py-3 px-4">{s.nombre_producto}</td>
                          <td className="py-3 px-4 text-red-600 font-bold">{s.cantidad_entregada}</td>
                          <td className="py-3 px-4">{s.num_entregas}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === 'duplicidades' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-lg text-gray-800">Reporte de Duplicidades</h3>
                  <button onClick={() => descargarCSV(datos.detalle, 'reporte_duplicidades')}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition">
                    <Download size={16} /> Descargar CSV
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {datos.resumen?.map((r, i) => (
                    <div key={i} className="bg-white rounded-xl shadow-sm p-4">
                      <p className="text-sm text-gray-500 capitalize">{r.tipo_duplicidad?.replace('_', ' ')} — {r.estatus}</p>
                      <p className="text-3xl font-bold text-gray-800">{r.total}</p>
                    </div>
                  ))}
                </div>
                <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
                  <table className="w-full text-sm min-w-[700px]">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Persona</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Teléfono</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Tipo</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Estatus</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datos.detalle?.map((d, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium">{d.nombre} {d.apellido_paterno}</td>
                          <td className="py-3 px-4">{d.telefono_principal}</td>
                          <td className="py-3 px-4 capitalize">{d.tipo_duplicidad?.replace('_', ' ')}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              d.estatus === 'resuelto' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>{d.estatus}</span>
                          </td>
                          <td className="py-3 px-4">{new Date(d.fecha_deteccion).toLocaleDateString('es-MX')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === 'visitas' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-lg text-gray-800">Reporte de Visitas</h3>
                  <button onClick={() => descargarCSV(datos.por_seccion, 'reporte_visitas')}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition">
                    <Download size={16} /> Descargar CSV
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {datos.por_resultado?.map((r, i) => (
                    <div key={i} className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-blue-500">
                      <p className="text-xs text-gray-500 capitalize">{r.resultado_visita?.replace('_', ' ')}</p>
                      <p className="text-2xl font-bold text-gray-800">{r.total}</p>
                    </div>
                  ))}
                </div>
                <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
                  <table className="w-full text-sm min-w-[700px]">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Sección</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Municipio</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Total Visitas</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Contactados</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Con Propaganda</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datos.por_seccion?.length === 0 ? (
                        <tr><td colSpan="5" className="py-6 text-center text-gray-400">Sin visitas registradas</td></tr>
                      ) : datos.por_seccion?.map((s, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 font-bold">{s.numero_seccion}</td>
                          <td className="py-3 px-4">{s.municipio}</td>
                          <td className="py-3 px-4 font-bold">{s.total_visitas}</td>
                          <td className="py-3 px-4 text-green-600 font-bold">{s.contactados}</td>
                          <td className="py-3 px-4 text-purple-600 font-bold">{s.con_propaganda}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <p className="text-center text-gray-400 text-xs mt-8">
          DOMINIUM © 2026 — Zacatlán, Puebla | GARCOR Design
        </p>
      </main>
    </div>
  );
}
